import { useEffect, useRef, useState } from "react";

const products = [
  {
    id: "vpn",
    name: "KESTEN VPN",
    tagline: "Secure. Private. Always On.",
    description:
      "A professional-grade VPN built exclusively for iOS. KESTEN VPN wraps every connection in AES-256 encryption, masks your IP address, and routes your traffic through a global network of secure servers — giving you real privacy on any network.",
    features: [
      "AES-256 military-grade encryption",
      "Zero-logs policy — your data stays yours",
      "One-tap secure connection",
      "Global server coverage",
      "Automatic kill switch protection",
      "IKEv2 & WireGuard protocol support",
    ],
    icon: "ri-shield-check-fill",
    image:
      "https://public.readdy.ai/ai/img_res/5f6c9bb7deb892dac267d86cde765cca.jpg",
    accent: "from-[#111] to-[#1a1a1a]",
    iconBg: "bg-emerald-500/20",
    iconColor: "text-emerald-400",
    featureColor: "text-emerald-500",
  },
  {
    id: "cleaner",
    name: "KESTEN Cleaner",
    tagline: "Clear the Clutter. Reclaim Your Device.",
    description:
      "KESTEN Cleaner intelligently scans your iPhone for junk files, redundant caches, duplicate photos, and unused data — then clears them in seconds. The result: more storage, faster performance, and a device that feels brand new.",
    features: [
      "Smart junk file detection & removal",
      "Duplicate photo finder",
      "Cache & temporary file cleanup",
      "Detailed storage breakdown analytics",
      "Large file manager",
      "Memory optimization boost",
    ],
    icon: "ri-sparkling-2-fill",
    image:
      "https://readdy.ai/api/search-image?query=iPhone%20displaying%20device%20cleaner%20storage%20optimization%20app%20with%20bright%20white%20minimal%20interface%2C%20large%20circular%20donut%20progress%20chart%20showing%20storage%20used%20in%20teal%20and%20light%20gray%2C%20elegant%20clean%20typography%2C%20storage%20categories%20listed%20below%20chart%2C%20professional%20product%20photography%20on%20soft%20white%20light%20gradient%20background%2C%20premium%20iOS%20app%20screenshot%2C%20ultra%20sharp%20render&width=520&height=320&seq=cleaner-product1&orientation=landscape",
    accent: "from-[#f9fafb] to-[#f3f4f6]",
    iconBg: "bg-emerald-100",
    iconColor: "text-emerald-600",
    featureColor: "text-emerald-600",
  },
];

export default function ProductsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="products" className="w-full bg-[#F8F9FA] py-[120px]" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-10">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-[12px] uppercase tracking-[3px] text-emerald-600 font-semibold">
            Our Products
          </span>
          <h2 className="text-[46px] font-bold text-[#0A0A0A] leading-[1.15] mt-4 mb-5">
            Apps That Make a Difference
          </h2>
          <p className="text-[18px] text-[#666] leading-[1.7] max-w-[560px] mx-auto">
            Two flagship iOS applications designed to protect your privacy and optimize your device performance.
          </p>
        </div>

        {/* Product Cards */}
        <div className="flex flex-col gap-8">
          {products.map((p, i) => (
            <div
              key={p.id}
              className={`rounded-2xl overflow-hidden transition-all duration-700 ${
                visible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${i * 150}ms` }}
            >
              <div
                className={`grid grid-cols-2 gap-0 bg-gradient-to-br ${p.accent} min-h-[340px]`}
              >
                {/* Text side */}
                <div className={`p-12 flex flex-col justify-center ${i === 0 ? "order-1" : "order-2"}`}>
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${p.iconBg} mb-6`}>
                    <i className={`${p.icon} text-3xl ${p.iconColor}`} />
                  </div>
                  <h3 className={`text-[28px] font-bold mb-2 ${i === 0 ? "text-white" : "text-[#0A0A0A]"}`}>
                    {p.name}
                  </h3>
                  <p className={`text-[14px] font-medium mb-4 ${i === 0 ? "text-emerald-400" : "text-emerald-600"}`}>
                    {p.tagline}
                  </p>
                  <p className={`text-[15px] leading-[1.75] mb-6 ${i === 0 ? "text-white/70" : "text-[#555]"}`}>
                    {p.description}
                  </p>
                  <ul className="space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-center gap-3">
                        <i className={`ri-check-line text-base ${p.featureColor} flex-shrink-0`} />
                        <span className={`text-[14px] ${i === 0 ? "text-white/80" : "text-[#444]"}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image side */}
                <div className={`relative overflow-hidden ${i === 0 ? "order-2" : "order-1"}`}>
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
